import { useState } from 'react';
import { useTranslation } from '../providers/UIProvider';
import { Button } from './Button';
import { Modal } from './Modal';
import { Textarea } from './Textarea';

/** Content longer than this offers the modal editor. */
const EXPAND_THRESHOLD_CHARS = 300;

export interface ExpandableTextareaProps {
  /** Id of the inline field, so an `InputWrapper` label points at it. */
  readonly id?: string;
  /** Modal title. */
  readonly label?: string;
  /** Accessible name of the inline field when no visible label points at it. */
  readonly 'aria-label'?: string;
  /** Ids of the hint or error text describing the inline field. */
  readonly 'aria-describedby'?: string;
  /** Marks the inline field invalid, as a wrapping `InputWrapper` in error does. */
  readonly 'aria-invalid'?: boolean;
  readonly value: string;
  /** Maximum visible rows — the field shrinks to the content when it is shorter. */
  readonly rows?: number;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly className?: string;
  /** Validation message shown inside the modal editor. */
  readonly errorText?: string | null;
  /** Grows with its flex row instead of its content — pair with a taller sibling. */
  readonly isStretch?: boolean;
  onChange(value: string): void;
}

const MIN_ROWS = 2;

/** Visible rows follow the content, capped by maxRows. */
const fitRows = (value: string, maxRows: number): number =>
  Math.min(Math.max(value.split('\n').length, MIN_ROWS), maxRows);

/** Textarea whose long content opens in a large modal editor. */
export const ExpandableTextarea = ({
  id,
  label,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid = false,
  value,
  rows = 6,
  placeholder,
  disabled = false,
  className,
  errorText = null,
  isStretch = false,
  onChange
}: ExpandableTextareaProps) => {
  const t = useTranslation();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const isExpandable = !disabled && value.length > EXPAND_THRESHOLD_CHARS;

  return (
    <div className={['relative flex flex-col', isStretch && 'flex-1'].filter(Boolean).join(' ')}>
      <Textarea
        id={id}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        // Textarea sets aria-invalid (and the error style) from hasError.
        hasError={ariaInvalid}
        size='full'
        rows={fitRows(value, rows)}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        // size='full' pins min-h-32 — drop it so the fitted rows actually drive the height.
        className={['min-h-0', isStretch && 'flex-1', className].filter(Boolean).join(' ')}
        onChange={event => onChange(event.target.value)}
      />
      {isExpandable && (
        // Floated over the field — in the flow it would offset whatever sits beside it.
        <Button
          size='sm'
          variant='default'
          onClick={() => setIsEditorOpen(true)}
          className='absolute bottom-2xs right-5 shadow-sm'
        >
          {t('ExpandableTextarea.openInEditor')}
        </Button>
      )}

      <Modal open={isEditorOpen} onClose={() => setIsEditorOpen(false)} size='full'>
        <Modal.Header onClose={() => setIsEditorOpen(false)}>
          {label ?? t('ExpandableTextarea.editorTitle')}
        </Modal.Header>
        <Modal.Content>
          <Textarea
            size='full'
            rows={24}
            value={value}
            className={`${className ?? ''} min-h-[70vh]`}
            onChange={event => onChange(event.target.value)}
          />
          {errorText !== null && <p className='text-sm text-error-text'>{errorText}</p>}
        </Modal.Content>
      </Modal>
    </div>
  );
};

ExpandableTextarea.displayName = 'ExpandableTextarea';
